import React from 'react';
import { isCounselNote, parseLegalMarkdown, renderLegalBlocks } from './legalMarkdown';
import styles from './LegalPage.module.css';

export const BLOCK_CLASSES = {
  h1: styles.title,
  h2: styles.sectionHeading,
  h3: styles.subHeading,
  p: styles.paragraph,
  quote: styles.callout,
  list: styles.list,
  hr: styles.rule,
};

/**
 * Blocks shown on the public page: everything except `[Counsel note: …]`
 * paragraphs. The server already strips them from the public endpoint; this is
 * defence in depth, and it is what the admin preview uses on the raw draft.
 *
 * @param {string} markdown - Document body.
 * @returns {Array<object>} Parsed blocks.
 */
export const publicLegalBlocks = (markdown) =>
  parseLegalMarkdown(markdown || '').filter((block) => !isCounselNote(block));

/**
 * Number of counsel-note paragraphs a body carries (hidden from the public).
 *
 * @param {string} markdown - Document body.
 * @returns {number}
 */
export const countCounselNotes = (markdown) =>
  parseLegalMarkdown(markdown || '').filter(isCounselNote).length;

/**
 * Format an ISO timestamp as e.g. "September 29, 2026"; null when missing or
 * unparseable.
 *
 * @param {string|null|undefined} iso
 * @returns {string|null}
 */
export const formatLegalDate = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

/**
 * LegalDocumentView — the document body exactly as /privacy and /terms show
 * it: the draft badge (until counsel approves), the title, a
 * "Version … · Published …" line, then the document. Shared by LegalPage and
 * the admin editor's preview so the two can't drift apart.
 *
 * @param {object} props
 * @param {Array<object>} props.blocks - Output of publicLegalBlocks().
 * @param {string} props.fallbackTitle - h1 used when the body has no `#` heading.
 * @param {string} [props.version] - Version label.
 * @param {string|null} [props.publishedAt] - ISO timestamp; null for an unpublished draft.
 * @param {boolean} props.counselApproved - False shows the DRAFT badge.
 * @param {string} [props.className] - Extra class on the <article>.
 */
const LegalDocumentView = ({
  blocks,
  fallbackTitle,
  version,
  publishedAt = null,
  counselApproved,
  className = '',
  ...rest
}) => {
  // Put the version line directly under the document's own title when it
  // opens with one; otherwise under the fallback title.
  const leadsWithTitle = blocks.length > 0 && blocks[0].type === 'h1';
  const hasTitle = blocks.some((block) => block.type === 'h1');
  const titleBlocks = leadsWithTitle ? blocks.slice(0, 1) : [];
  const bodyBlocks = leadsWithTitle ? blocks.slice(1) : blocks;
  const published = formatLegalDate(publishedAt);

  return (
    <article className={`${styles.article} ${className}`.trim()} {...rest}>
      {!counselApproved && (
        <p className={styles.draftBadge} data-testid="legal-draft-badge">
          DRAFT — needs counsel review
        </p>
      )}
      {!hasTitle && <h1 className={styles.title}>{fallbackTitle}</h1>}
      <React.Fragment key="title">{renderLegalBlocks(titleBlocks, BLOCK_CLASSES)}</React.Fragment>
      {version && (
        <p className={styles.meta} data-testid="legal-version-meta">
          {`Version ${version} · ${published ? `Published ${published}` : 'Not yet published'}`}
        </p>
      )}
      <React.Fragment key="body">{renderLegalBlocks(bodyBlocks, BLOCK_CLASSES)}</React.Fragment>
    </article>
  );
};

export default LegalDocumentView;
